import { withHydrate } from '@relay/react/hooks';
import ApiService from '@core/api/services/ApiService';
import { Theme } from '@core/enums';
import UrlResolver from '@core/url-resolver/UrlResolver';
import { Device, until } from '@helpers/media';
import { getNavigationId } from '@helpers/navigationTitle';
import { useSlides } from '@helpers/slick';
import { isSSR } from '@helpers/ssr';
import withRedux from '@helpers/withRedux';
import { useInView } from '@hooks/useInView';
import useMedia from '@hooks/useMedia';
import CardSlider, { CardType } from '@stories/Components/Media/CardSlider/CardSlider';
import PerfectMatchCard from '@stories/Elements/Cards/PerfectMatchCard/PerfectMatchCard';
import ProductCard from '@stories/Elements/Cards/ProductCard/ProductCard';
import Link from '@stories/Elements/Forms/Buttons/Link';
import { Grid } from '@stories/Settings/Grids/Grid.styles';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProductSuggestionsStyles as S } from './ProductSuggestions.styles';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export let TabId = /*#__PURE__*/function (TabId) {
  TabId["PerfectMatch"] = "perfect-match";
  TabId["ToolSpecific"] = "tool-specific";
  TabId["ToolSibling"] = "tool-sibling";
  TabId["ToolGeneric"] = "tool-generic";
  return TabId;
}({});
const ProductSuggestions = props => {
  if (isSSR) {
    return null;
  }
  const dispatch = useDispatch();
  const relations = useSelector(x => x.productDetail.relations);
  const modelAgilityId = useSelector(state => state.productDetail.modelAgilityId);
  const pageContext = useSelector(state => state.pageContext);
  const responsiveThreshold = useMedia([until(Device.Tablet)], [0.1], 0.3);
  const [elementRef, inView] = useInView({
    threshold: responsiveThreshold
  });
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [accessoryPage, setAccessoryPage] = useState(null);
  const [slidesToShow, responsiveConfig] = useSlides([until(Device.MobileLarge), until(Device.TabletLarge), until(Device.Desktop)], [1, 2, 3], 4);
  const fetchProducts = async () => {
    dispatch({
      type: 'PRODUCT_DETAIL/SET_RELATIONS_LOADING',
      payload: true
    });
    const response = await ApiService.request({
      method: 'GET',
      baseUrl: UrlResolver.getDomain(),
      controller: 'product-detail',
      slug: 'product-relations',
      params: {
        cultureCode: pageContext.documentCulture,
        modelAgilityId,
        published: pageContext.showOnlyPublishedProducts
      }
    });
    if (response.hasAnyRelations) {
      const resTabs = [];
      if (response.perfectMatch && response.perfectMatch.length > 0) {
        resTabs.push({
          id: TabId.PerfectMatch,
          title: 'custom.productsuggestions.perfectmatch',
          products: response.perfectMatch
        });
      }
      if (response.toolSpecific && response.toolSpecific.length > 0) {
        resTabs.push({
          id: TabId.ToolSpecific,
          title: 'custom.productsuggestions.toolspecific',
          products: response.toolSpecific
        });
      }
      if (response.siblingProducts && response.siblingProducts.length > 0) {
        resTabs.push({
          id: TabId.ToolSibling,
          title: 'custom.productsuggestions.toolsibling',
          products: response.siblingProducts
        });
      }
      if (response.generic && response.generic.length > 0) {
        resTabs.push({
          id: TabId.ToolGeneric,
          title: 'custom.productsuggestions.toolgeneric',
          products: response.generic
        });
      }
      if (response.perfectMatchAccessoryPage && response.perfectMatchAccessoryPage.length > 0) {
        setAccessoryPage(response.perfectMatchAccessoryPage);
      }
      setTabs(resTabs);
    }
    dispatch({
      type: 'PRODUCT_DETAIL/SET_RELATIONS',
      payload: response
    });
    dispatch({
      type: 'PRODUCT_DETAIL/SET_RELATIONS_LOADING',
      payload: false
    });
  };
  useMemo(() => {
    fetchProducts();
  }, [modelAgilityId]);
  if (relations && !relations.hasAnyRelations) {
    return null;
  }
  return /*#__PURE__*/_jsx(S.Outer, {
    id: getNavigationId(props.navigationTitle),
    "data-navigation-title": props.navigationTitle,
    "data-navigation-id": getNavigationId(props.navigationTitle),
    children: tabs.length > 0 && /*#__PURE__*/_jsx(S.Wrapper, {
      ref: elementRef,
      "data-theme": tabs[activeTab].id,
      children: /*#__PURE__*/_jsx(Grid.Default, {
        children: /*#__PURE__*/_jsxs(S.Inner, {
          children: [props.translations['custom.productsuggestions.title'] && /*#__PURE__*/_jsx(S.Title, {
            inView: inView,
            children: /*#__PURE__*/_jsx("span", {
              children: props.translations['custom.productsuggestions.title']
            })
          }), tabs.length > 1 && /*#__PURE__*/_jsx(S.Tabs, {
            children: tabs.map((tab, i) => /*#__PURE__*/_jsx(S.Tab, {
              isActive: i == activeTab,
              onClick: () => setActiveTab(i),
              children: props.translations[tab.title]
            }, `ProductSuggestions_Tab_${i}`))
          }), tabs[activeTab].id == TabId.PerfectMatch ? /*#__PURE__*/_jsxs(S.PerfectMatchGrid, {
            children: [/*#__PURE__*/_jsx(ProductCard, {
              product: {
                ...tabs[activeTab].products[0],
                includedProducts: undefined
              },
              buttonText: props.translations['custom.products.view'] || '',
              isLink: false,
              index: 0
            }), /*#__PURE__*/_jsx(S.PerfectMatchPlus, {}), /*#__PURE__*/_jsx(ProductCard, {
              product: {
                ...tabs[activeTab].products[1],
                includedProducts: undefined
              },
              buttonText: props.translations['custom.products.view'] || '',
              index: 1,
              target: '_blank'
            }, tabs[activeTab].products[1].agilityId), /*#__PURE__*/_jsx(PerfectMatchCard, {
              translations: props.translations,
              storeLocatorUrl: props.storeLocatorUrl,
              accessoryPage: accessoryPage,
              product: tabs[activeTab].products[0]
            })]
          }, `ProductSuggestions_PerfectMatchGrid_${TabId.PerfectMatch}`) : /*#__PURE__*/_jsx(S.SliderWrapper, {
            children: tabs[activeTab].products.length > slidesToShow ? /*#__PURE__*/_jsx(CardSlider, {
              cardType: CardType.Product,
              items: tabs[activeTab].products,
              slidesToShow: slidesToShow,
              responsiveConfig: responsiveConfig,
              buttonText: props.translations['custom.products.view'],
              cardTheme: Theme.White
            }) : /*#__PURE__*/_jsx(S.SlidesGrid, {
              slidesToShow: slidesToShow,
              children: tabs[activeTab].products.map((item, index) => /*#__PURE__*/_jsx(ProductCard, {
                product: {
                  ...item,
                  includedProducts: undefined
                },
                buttonText: props.translations['custom.products.view'] || '',
                index: index
              }, item.agilityId))
            })
          }, `ProductSuggestions_SliderWrapper_${tabs[activeTab].id}`), props.viewAllButtonText && props.viewAllButtonUrl && /*#__PURE__*/_jsx(S.ButtonWrapper, {
            inView: inView,
            children: /*#__PURE__*/_jsx(Link, {
              href: props.viewAllButtonUrl,
              text: props.viewAllButtonText,
              theme: "black-grey",
              icon: "arrow"
            })
          })]
        })
      })
    })
  });
};
export const clientConfig = {
  hydrate: 'always'
};
export default withHydrate(withRedux(ProductSuggestions), 'ProductSuggestions', 'hybrid');
